import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import TButton from "@/components/TButton";
import { ShieldAlert, Lock, AtSign, Loader2, Terminal as TermIcon } from "lucide-react";
import { toast } from "sonner";

const DEMO = [
  { label: "Admin", email: process.env.REACT_APP_DEMO_ADMIN_EMAIL, password: process.env.REACT_APP_DEMO_ADMIN_PASSWORD },
  { label: "Analyst", email: process.env.REACT_APP_DEMO_ANALYST_EMAIL, password: process.env.REACT_APP_DEMO_ANALYST_PASSWORD },
  { label: "Viewer", email: process.env.REACT_APP_DEMO_VIEWER_EMAIL, password: process.env.REACT_APP_DEMO_VIEWER_PASSWORD },
];

const Login = () => {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState(process.env.REACT_APP_DEMO_ADMIN_EMAIL || "");
  const [password, setPassword] = useState(process.env.REACT_APP_DEMO_ADMIN_PASSWORD || "");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Authenticated. Welcome to CyberSphere X.");
      nav("/");
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const msg = typeof raw === "string" ? raw
                : Array.isArray(raw) ? raw.map((r) => r?.msg || JSON.stringify(r)).join("; ")
                : (raw && typeof raw === "object") ? (raw.msg || JSON.stringify(raw))
                : "Login failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const fillDemo = (d) => {
    setEmail(d.email);
    setPassword(d.password);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden">
      {/* HUD frame */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-8 left-8 text-[10px] tracking-[0.4em] text-cyan-400/70">SECURE TERMINAL // V1.0</div>
        <div className="absolute top-8 right-8 text-[10px] tracking-[0.4em] text-fuchsia-400/70">CSX-CORE-01</div>
        <div className="absolute bottom-8 left-8 text-[10px] tracking-[0.4em] text-slate-500">ZERO-TRUST GATEWAY</div>
        <div className="absolute bottom-8 right-8 text-[10px] tracking-[0.4em] text-slate-500">TLS 1.3 // mTLS</div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 max-w-5xl w-full">
        {/* Brand panel */}
        <div className="csx-card p-8 hidden md:flex flex-col justify-between">
          <div>
            <div className="font-display text-3xl font-black tracking-[0.15em] mb-2">
              <span className="text-cyan-400 text-glow-cyan">CYBER</span>
              <span className="text-fuchsia-400 text-glow-pink">SPHERE</span>
              <span className="text-cyan-400 text-glow-cyan"> X</span>
            </div>
            <div className="text-xs uppercase tracking-[0.3em] text-slate-500 mb-6">
              Next-Gen Cyber Security Validation
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">
              Continuous attack-surface discovery, autonomous red teaming, AI-driven blue team,
              and executive cyber intelligence — fused into a single operational platform.
            </p>
          </div>

          <div className="space-y-2 text-xs">
            {[
              ["Autonomous Red Team", "ACTIVE"],
              ["AI Cyber Twin", "ONLINE"],
              ["Predictive Engine", "v4.2-RC"],
              ["Compliance AI", "94 / 81 / 97 / 67"],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-slate-800 py-1.5">
                <span className="text-slate-400 uppercase tracking-widest">{k}</span>
                <span className="text-cyan-300 tick-shadow">{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="csx-card p-8">
          <div className="flex items-center gap-2 text-cyan-300 text-xs tracking-[0.3em] uppercase mb-6">
            <ShieldAlert className="h-4 w-4" />
            Authentication Required
          </div>

          <form onSubmit={submit} className="space-y-5">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.25em] text-slate-400">Operator Email</span>
              <div className="mt-1.5 flex items-center gap-2 border border-slate-800 bg-slate-950 px-3 py-2.5 focus-within:border-cyan-500/70 focus-within:shadow-[0_0_12px_rgba(6,182,212,0.25)]">
                <AtSign className="h-4 w-4 text-slate-500" />
                <input
                  data-testid="login-email"
                  className="flex-1 bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-600"
                  placeholder="operator@org.local"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </label>

            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.25em] text-slate-400">Pass-Phrase</span>
              <div className="mt-1.5 flex items-center gap-2 border border-slate-800 bg-slate-950 px-3 py-2.5 focus-within:border-cyan-500/70 focus-within:shadow-[0_0_12px_rgba(6,182,212,0.25)]">
                <Lock className="h-4 w-4 text-slate-500" />
                <input
                  data-testid="login-password"
                  type="password"
                  className="flex-1 bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-600"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </label>

            <TButton data-testid="login-submit" type="submit" disabled={busy} className="w-full" icon={busy ? Loader2 : TermIcon}>
              {busy ? "Authenticating..." : "Engage // Login"}
            </TButton>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-800">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Demo Operators</div>
            <div className="grid grid-cols-3 gap-2">
              {DEMO.map((d) => (
                <button
                  key={d.email}
                  data-testid={`demo-${d.label.toLowerCase()}`}
                  onClick={() => fillDemo(d)}
                  className="text-[10px] uppercase tracking-widest border border-slate-800 hover:border-cyan-500/60 hover:text-cyan-300 px-2 py-1.5 text-slate-400 transition-all"
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
